import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  FC,
  forwardRef,
  HTMLProps,
  MouseEvent,
  MouseEventHandler,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import classNames from 'classnames';

import Spinner from '../Spinner';

import './Button.scss';

import {
  AnalyticsContext,
  handleAnalyticsEvent,
  TAnalyticsEventData,
  THandleAnalyticsEvent,
} from '@dtci/dash/analytics';

import { FontAwesomePackage, TestMetadata } from '../types';

const BUTTON_CLASS_CONST = 'sb-button_';

export interface ButtonProps
  extends DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  /** Set the button style: promoted (default), standard, demoted, link */
  styleType?: 'promoted' | 'standard' | 'demoted' | 'link';
  /** Set the button color: primary (default), secondary, success, error, warning */
  colorType?:
    | 'primary'
    | 'secondary'
    | 'base'
    | 'success'
    | 'error'
    | 'warning';
  /** Set the icon position: before (default), after, stacked */
  iconPlacement?: 'before' | 'after' | 'stacked';
  /** Can change the size of the button: smaller, small, medium (default), large, auto */
  sizeType?: 'smaller' | 'small' | 'medium' | 'large' | 'auto';
  /** Name of Font Awesome icon to show or custom icon component */
  iconType?: string | JSX.Element;
  /** Package of Font Awesome icon */
  iconPackage?: FontAwesomePackage;
  /** Boolean to determine if button has an icon and no label */
  iconOnly?: boolean;
  /** Set label text for accessibility, should match button text **/
  labelText?: string;
  /** shakeOnDisabledHover */
  shakeOnDisabledHover?: boolean;
  /** Indicates if button should be disabled */
  disabled?: boolean;
  /** Indicates an async loading state */
  isFetching?: boolean;
  /** Set the button to pressed state */
  pressed?: boolean;
  /** onClick event handler  */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  // Test IDs for sections of Button
  testMetadata?: TestMetadata;
  /** Border Flush */
  flush?: 'left' | 'right' | 'flush';
  /** Additional event information to pass to analytics tracking */
  analyticsEventData?: TAnalyticsEventData;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      colorType = 'primary',
      styleType = 'promoted',
      labelText,
      iconType,
      iconPackage = 'fas',
      iconPlacement = 'before',
      sizeType,
      iconOnly,
      children,
      shakeOnDisabledHover,
      disabled,
      isFetching = false,
      pressed,
      onClick,
      testMetadata,
      flush,
      analyticsEventData,
      ...otherProps
    },
    ref,
  ) => {
    const isStackedButton = iconPlacement === 'stacked';

    const analyticsContext = useContext(AnalyticsContext);

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (isFetching) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onClick?.(event);

        if (analyticsEventData) {
          handleAnalyticsEvent({
            analyticsContext,
            analyticsEventData,
            componentType: 'Button',
            actionName: 'click',
            label:
              otherProps?.['aria-label'] ||
              labelText ||
              children?.toString() ||
              iconType?.toString() ||
              '',
          } as THandleAnalyticsEvent);
        }
      },
      [
        isFetching,
        onClick,
        analyticsContext,
        otherProps,
        labelText,
        children,
        iconType,
        analyticsEventData,
      ],
    );

    const buttonClassNames = useMemo(
      () =>
        classNames(
          'sb-button',
          {
            [BUTTON_CLASS_CONST + colorType]: colorType,
            [BUTTON_CLASS_CONST + styleType]: styleType,
            [BUTTON_CLASS_CONST + sizeType]: sizeType,
            ['sb-button_icon-' + iconPlacement]: iconType && iconPlacement,
            'sb-button_icon-only': iconOnly,
            'sb-button_no-icon': !iconType,
            'sb-button_fetching': isFetching,
            'sb-button_pressed': pressed,
            'sb-button_disabled': isStackedButton && disabled, // only stacked needs to have a class for disabled
            'sb-shake-on-disabled': shakeOnDisabledHover,
            'sb-button_async': isFetching,
            'sb-button__flush-right': flush === 'right',
            'sb-button__flush': flush === 'flush',
            'sb-button__flush-left': flush === 'left',
          },
          className,
        ),
      [
        colorType,
        styleType,
        iconPlacement,
        iconOnly,
        shakeOnDisabledHover,
        className,
        disabled,
        isStackedButton,
        sizeType,
        isFetching,
        pressed,
        iconType,
        flush,
      ],
    );

    const stackedButtonWrapperClassNames = useMemo(
      () =>
        classNames('sb-stacked-wrapper', {
          'sb-stacked-wrapper-disabled': disabled,
        }),
      [disabled],
    );

    const loader = (
      <div className="sb-button_spinner-container">
        <Spinner size="small" className="sb-button_spinner" />
      </div>
    );

    const icon = (
      <span className="sb-button_icon-container">
        {typeof iconType === 'string' ? (
          <>
            {isFetching && loader}
            <i
              className={`${iconPackage} ${iconType} sb-button_icon`}
              data-testid={
                testMetadata?.['data-testid'] ? 'button-icon' : undefined
              }
            />
          </>
        ) : (
          <>
            {isFetching && loader}
            <span className="sb-button_icon">{iconType}</span>
          </>
        )}
      </span>
    );

    return (
      <>
        {!isStackedButton && (
          <button
            {...otherProps}
            ref={ref}
            className={buttonClassNames}
            aria-label={labelText}
            title={labelText}
            disabled={disabled}
            onClick={handleClick}
            {...testMetadata}
          >
            {iconType &&
              (iconOnly || !iconPlacement || iconPlacement === 'before') &&
              icon}

            {!iconOnly && <span className="sb-button-text">{children}</span>}
            {isFetching && !iconType && loader}
            {iconType && !iconOnly && iconPlacement === 'after' && icon}
          </button>
        )}

        {isStackedButton && (
          <button
            {...otherProps}
            disabled={disabled}
            aria-label={labelText}
            ref={ref}
            title={labelText}
            className={stackedButtonWrapperClassNames}
            onClick={handleClick}
          >
            <span className={buttonClassNames}>{icon}</span>
            <div className="sb-stacked-label">{children}</div>
          </button>
        )}
      </>
    );
  },
);

Button.displayName = 'Button';

export default Button as FC<HTMLProps<HTMLButtonElement> & ButtonProps>;
